import React, { useEffect, useRef } from "react"
//@ts-ignore
import campfire from "audio/campfire.mp3"
//@ts-ignore
import sleighBells from "audio/sleigh-bells.mp3"
import { useKeyboardOpen } from "@/hooks/useKeyboardOpen"

export function Snow() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let W = window.innerWidth
    let H = window.innerHeight
    canvas.width = W
    canvas.height = H

    let animationFrameId: number

    class Particle {
      x: number
      y: number
      vx: number
      vy: number
      radius: number
      color: string

      constructor() {
        this.x = Math.random() * W
        this.y = Math.random() * H
        this.vx = Math.random() * 4 - 1
        this.vy = Math.random() * 4 + 1
        const a = Math.random()
        this.color = `rgba(255, 255, 255, ${a})`
        this.radius = Math.random() * 2 + 2
      }

      update() {
        this.x += this.vx
        this.y += this.vy

        // Boundaries
        if (this.x < -50) this.x = W + 50
        if (this.y < -50) this.y = H + 50
        if (this.x > W + 50) this.x = -50
        if (this.y > H + 50) this.y = -50
      }

      draw(ctx: CanvasRenderingContext2D) {
        ctx.beginPath()
        const gradient = ctx.createRadialGradient(
          this.x,
          this.y,
          0,
          this.x,
          this.y,
          this.radius,
        )
        gradient.addColorStop(0, this.color)
        gradient.addColorStop(1, "rgb(66, 66, 66)") // Kept original gradient end color

        ctx.fillStyle = gradient
        ctx.arc(this.x, this.y, this.radius, Math.PI * 2, 0)
        ctx.fill()
      }
    }

    // Adjust particle count based on screen width
    const particleCount = window.innerWidth < 768 ? 50 : 100
    const particles: Particle[] = []

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle())
    }

    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D

    function animate() {
      ctx.clearRect(0, 0, canvas!.width, canvas!.height)

      for (const p of particles) {
        p.draw(ctx)
        p.update()
      }

      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    const handleResize = () => {
      W = window.innerWidth
      H = window.innerHeight
      canvas.width = W
      canvas.height = H
      // Re-initialize particles on resize if desired, or let them flow
    }

    window.addEventListener("resize", handleResize)

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 z-99999 opacity-10 pointer-events-none h-screen w-screen overflow-hidden bg-[radial-gradient(ellipse_at_bottom,#1b2735_90%,#090a0f_100%)]"
    />
  )
}
export function ChristmasTree({ onInteract }: { onInteract?: () => void }) {
  function playSound() {
    let audio1 = document.getElementById("campfire") as HTMLAudioElement
    let audio2 = document.getElementById("sleigh-bells") as HTMLAudioElement

    if (audio1.paused) {
      audio1.play()
      audio2.play()
      onInteract?.()
      return
    }
    audio1.pause()
    audio2.pause()
  }
  return (
    <>
      <svg
        width="100"
        height="100"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="fixed bottom-0"
        onClick={() => playSound()}
      >
        <g clipPath="url(#clip0_117_217)">
          <path
            d="M31.0345 75.8621H44.8276V96.5517H31.0345V75.8621Z"
            fill="#805333"
          />
          <path
            d="M56.8966 50C58.5451 57.0301 62.8844 63.1335 68.9828 67L68.9655 78.7241C67.9708 79.0556 66.9277 79.2187 65.8793 79.2069C62.8966 79.2426 59.9461 78.5876 57.2586 77.2931L53.3621 79.1552C52.8045 81.6397 51.8559 84.0199 50.5517 86.2069H43.8103C41.5726 84.1219 39.5981 81.7712 37.931 79.2069C33.931 85.4655 25.8621 86.2069 25.8621 86.2069C22.569 82.7586 23.1724 82.1207 22.5 79.1552L18.6034 77.2931C17.0794 78.0337 15.4608 78.5617 13.7931 78.8621L2.63793 75.8621C1.40894 74.3982 0.509269 72.6871 0 70.8448C9.7069 66.4828 18.9655 56.8966 20.6897 50H56.8966Z"
            fill="#33907C"
          />
          <path
            d="M50 33.369C54.4481 41.5594 61.4164 48.0991 69.8724 52.019C68.3852 55.7606 64.7763 58.2256 60.75 58.25C60.4641 58.2851 60.1759 58.2989 59.8879 58.2914C55.5059 58.4887 51.2162 56.9915 47.9086 54.1104C47.9086 54.1104 45.9172 64.5655 37.931 68.7466C29.9448 64.5655 27.95 54.1104 27.95 54.1104C24.6425 56.9915 20.3527 58.4887 15.9707 58.2914C15.6827 58.299 15.3946 58.2851 15.1086 58.25C11.0823 58.2256 7.47346 55.7606 5.98621 52.019C14.5081 48.078 21.5152 41.4697 25.9483 33.1931L50 33.369Z"
            fill="#14A085"
          />
          <path
            d="M32.9949 17.5155C28.8173 21.1052 23.9362 26.6535 13.9828 31.1035C15.5327 34.957 19.3319 37.4255 23.4828 37.2759C25.7534 37.077 27.9565 36.4013 29.9483 35.2931C30.2665 38.5572 31.9357 41.5394 34.5518 43.5173H41.3104C43.9264 41.5394 45.5956 38.5572 45.9138 35.2931C47.9056 36.4013 50.1087 37.077 52.3793 37.2759C56.5302 37.4255 60.3294 34.957 61.8793 31.1035C51.9259 26.65 47.0518 21.1035 42.8673 17.5155H32.9949Z"
            fill="#26B99A"
          />
          <path
            d="M38.7741 0.544829L41.7707 6.92414C41.9013 7.21027 42.1668 7.4117 42.4776 7.46035L49.2017 8.48449C49.5576 8.55112 49.8476 8.80866 49.9559 9.15413C50.0642 9.49959 49.9731 9.87663 49.7189 10.1345L44.8379 15.1345C44.6198 15.3605 44.5219 15.6765 44.5741 15.9862L45.7224 23.0207C45.7924 23.3774 45.6516 23.7426 45.3603 23.9599C45.069 24.1773 44.6788 24.2083 44.3569 24.0397L38.3707 20.7362C38.096 20.5843 37.7626 20.5843 37.4879 20.7362L31.5017 24.0397C31.1797 24.2083 30.7896 24.1773 30.4983 23.9599C30.207 23.7426 30.0662 23.3774 30.1362 23.0207L31.2845 15.9862C31.3366 15.6765 31.2388 15.3605 31.0207 15.1345L26.1396 10.1345C25.8855 9.87663 25.7944 9.49959 25.9027 9.15413C26.0109 8.80866 26.301 8.55112 26.6569 8.48449L33.381 7.46035C33.6918 7.4117 33.9573 7.21027 34.0879 6.92414L37.0845 0.55345C37.232 0.219816 37.5617 0.00391801 37.9265 0.0020568C38.2913 0.000195597 38.6232 0.212718 38.7741 0.544829Z"
            fill="#F0C419"
          />
          <path
            d="M37.931 49.9276C40.1364 49.9276 41.9241 48.0557 41.9241 45.7466C41.9241 43.4374 40.1364 41.5655 37.931 41.5655C35.7257 41.5655 33.9379 43.4374 33.9379 45.7466C33.9379 48.0557 35.7257 49.9276 37.931 49.9276Z"
            fill="#F0C419"
          />
          <path
            d="M21.9603 79.2C24.1657 79.2 25.9534 77.3281 25.9534 75.019C25.9534 72.7099 24.1657 70.838 21.9603 70.838C19.755 70.838 17.9672 72.7099 17.9672 75.019C17.9672 77.3281 19.755 79.2 21.9603 79.2Z"
            fill="#F0C419"
          />
          <path
            d="M53.9017 79.2C56.1071 79.2 57.8948 77.3281 57.8948 75.019C57.8948 72.7099 56.1071 70.838 53.9017 70.838C51.6964 70.838 49.9086 72.7099 49.9086 75.019C49.9086 77.3281 51.6964 79.2 53.9017 79.2Z"
            fill="#F0C419"
          />
          <path
            d="M51.9052 49.9293C53.0078 49.9293 53.9017 48.993 53.9017 47.8379C53.9017 46.6829 53.0078 45.7466 51.9052 45.7466C50.8025 45.7466 49.9086 46.6829 49.9086 47.8379C49.9086 48.993 50.8025 49.9293 51.9052 49.9293Z"
            fill="#ECF0F1"
          />
          <path
            d="M23.9569 49.9293C25.0595 49.9293 25.9534 48.993 25.9534 47.8379C25.9534 46.6829 25.0595 45.7466 23.9569 45.7466C22.8542 45.7466 21.9603 46.6829 21.9603 47.8379C21.9603 48.993 22.8542 49.9293 23.9569 49.9293Z"
            fill="#ECF0F1"
          />
          <path
            d="M8.62067 100H12.0689H43.1034V93.1035H13.7931L8.62067 100Z"
            fill="#F0C419"
          />
          <path
            d="M12.069 75.8621H1.72414C0.771923 75.8621 0 76.634 0 77.5862V98.2759C0 99.2281 0.771923 100 1.72414 100H12.069C13.0212 100 13.7931 99.2281 13.7931 98.2759V77.5862C13.7931 76.634 13.0212 75.8621 12.069 75.8621Z"
            fill="#E64C3C"
          />
          <path
            d="M98.2759 65.5172H70.6897C69.7374 65.5172 68.9655 66.2891 68.9655 67.2413V98.2758C68.9655 99.228 69.7374 100 70.6897 100H98.2759C99.2281 100 100 99.228 100 98.2758V67.2413C100 66.2891 99.2281 65.5172 98.2759 65.5172Z"
            fill="#E64C3C"
          />
          <path
            d="M22.4138 93.1035H25.8621V100H22.4138V93.1035Z"
            fill="#955BA5"
          />
          <path
            d="M8.62069 75.8621H5.17241V84.4828H0V87.931H5.17241V100H8.62069V87.931H13.7931V84.4828H8.62069V75.8621Z"
            fill="#F0C419"
          />
          <path
            d="M100 81.0345H86.2069V67.2414H89.6552C92.8432 67.2606 95.629 65.0922 96.3927 61.9969C97.1563 58.9015 95.6988 55.6862 92.8675 54.2205C90.0363 52.7547 86.5694 53.4206 84.4828 55.831C82.3947 53.4402 78.9424 52.7875 76.1256 54.251C73.3088 55.7145 71.8582 58.9145 72.614 61.9975C73.3699 65.0805 76.1361 67.2467 79.3103 67.2414H82.7586V81.0345H68.9655V84.4827H82.7586V100H86.2069V84.4827H100V81.0345ZM89.6552 56.8965C91.5596 56.8965 93.1034 58.4404 93.1034 60.3448C93.1034 62.2492 91.5596 63.7931 89.6552 63.7931H86.2069V60.3448C86.2069 58.4404 87.7507 56.8965 89.6552 56.8965ZM75.8621 60.3448C75.8621 58.4404 77.4059 56.8965 79.3103 56.8965C81.2148 56.8965 82.7586 58.4404 82.7586 60.3448V63.7931H79.3103C77.4059 63.7931 75.8621 62.2492 75.8621 60.3448Z"
            fill="#F0C419"
          />
          <path
            d="M58.6207 86.2069H41.3793C40.4271 86.2069 39.6552 86.9788 39.6552 87.931V98.2759C39.6552 99.2281 40.4271 100 41.3793 100H58.6207C59.5729 100 60.3448 99.2281 60.3448 98.2759V87.931C60.3448 86.9788 59.5729 86.2069 58.6207 86.2069Z"
            fill="#955BA5"
          />
          <path
            d="M48.2759 86.2069H51.7242V100H48.2759V86.2069Z"
            fill="#F3D55B"
          />
        </g>
        <defs>
          <clipPath id="clip0_117_217">
            <rect width="100" height="100" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <audio id="campfire" src={campfire} />
      <audio id="sleigh-bells" src={sleighBells} />
    </>
  )
}
export function ChristmasHat() {
  return (
    <svg
      width="100"
      height="100"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="fixed -right-[9px] bottom-[85px] z-2"
    >
      <g clipPath="url(#clip0_123_239)">
        <path
          d="M24.0888 39.5795C24.0888 39.5795 17.895 33.3077 6.9925 32.6549C4.46469 32.5035 2.41766 34.6686 2.71031 37.1841C3.42672 43.3427 5.86344 54.1263 14.1444 58.0342L24.0888 39.5795Z"
          fill="#E19974"
        />
        <path
          d="M17.3181 38.7909C18.125 37.9724 18.9687 37.2044 19.8437 36.4852C22.6159 38.0888 24.0888 39.5794 24.0888 39.5794L14.1445 58.0341C12.0602 57.0505 10.3464 55.631 8.93672 53.9594C10.2489 48.2869 13.1106 43.0599 17.3181 38.7909Z"
          fill="#DC8758"
        />
        <path
          d="M14.1445 58.0341C8.905 55.5614 6.00563 50.3363 4.40078 45.2716C4.25266 44.2108 4.15391 43.25 4.08859 42.4373C3.96469 40.8945 5.18562 39.5778 6.73328 39.5794C13.5102 39.5861 18.6511 41.8027 21.8489 43.7359L14.1445 58.0341Z"
          fill="#F4D7C8"
        />
        <path
          d="M15.4358 40.875C18.0566 41.6792 20.2089 42.7445 21.8491 43.7359L14.1445 58.0341C12.0598 57.0503 10.3467 55.6298 8.93703 53.958C10.0431 49.1781 12.2506 44.7155 15.4358 40.875Z"
          fill="#ECBBA3"
        />
        <path
          d="M55.9114 39.5795C55.9114 39.5795 62.1051 33.3077 73.0076 32.6549C75.5355 32.5035 77.5825 34.6686 77.2898 37.1841C76.5734 43.3427 74.1367 54.1263 65.8558 58.0342L55.9114 39.5795Z"
          fill="#E19974"
        />
        <path
          d="M62.682 38.7909C61.8751 37.9724 61.0314 37.2044 60.1564 36.4852C57.3842 38.0888 55.9114 39.5794 55.9114 39.5794L65.8556 58.0339C67.94 57.0503 69.6537 55.6308 71.0634 53.9592C69.7511 48.2869 66.8895 43.0599 62.682 38.7909Z"
          fill="#DC8758"
        />
        <path
          d="M65.8556 58.0341C71.0952 55.5614 73.9945 50.3363 75.5994 45.2716C75.7475 44.2108 75.8463 43.25 75.9116 42.4373C76.0355 40.8945 74.8145 39.5778 73.2669 39.5794C66.49 39.5861 61.3491 41.8027 58.1512 43.7359L65.8556 58.0341Z"
          fill="#F4D7C8"
        />
        <path
          d="M64.5642 40.875C61.9434 41.6792 59.7911 42.7445 58.1509 43.7359L65.8555 58.0341C67.9402 57.0503 69.6533 55.6298 71.063 53.958C69.957 49.1781 67.7494 44.7155 64.5642 40.875Z"
          fill="#ECBBA3"
        />
        <path
          d="M77.4967 1.67017C76.1269 0.760947 74.2799 1.1347 73.371 2.50439L67.3353 11.5995V4.22142C67.3353 2.60751 66.0803 1.23298 64.4674 1.17579C62.7741 1.11564 61.3827 2.47032 61.3827 4.15017V20.5696L58.0456 25.5981L54.5206 15.7063V10.5347C54.5206 8.92079 53.2656 7.54626 51.6527 7.48908C49.9594 7.42892 48.568 8.78361 48.568 10.4635V16.2208C48.568 16.5613 48.6264 16.8992 48.7408 17.2199L53.9342 31.7936L49.9985 37.7246C49.0896 39.0942 49.463 40.9413 50.8327 41.8502C52.2024 42.7591 54.0496 42.3857 54.9585 41.016L78.331 5.79579C79.2399 4.42626 78.8664 2.57907 77.4967 1.67017Z"
          fill="#D77F4A"
        />
        <path
          d="M53.5897 32.313L49.9984 37.7245C49.0895 39.0942 49.463 40.9412 50.8326 41.8501C52.2023 42.759 54.0495 42.3856 54.9584 41.0159L58.7095 35.3634C57.0922 34.1814 55.3791 33.1617 53.5897 32.313Z"
          fill="#C86D36"
        />
        <path
          d="M2.50344 1.67017C3.87328 0.760947 5.72031 1.1347 6.62922 2.50439L12.6648 11.5995V4.22142C12.6648 2.60751 13.9198 1.23298 15.5328 1.17579C17.2261 1.11564 18.6175 2.47032 18.6175 4.15017V20.5696L21.9545 25.5981L25.4795 15.7063V10.5347C25.4795 8.92079 26.7345 7.54626 28.3475 7.48908C30.0408 7.42892 31.4322 8.78361 31.4322 10.4635V16.2208C31.4322 16.5613 31.3738 16.8992 31.2594 17.2199L26.0659 31.7936L30.0017 37.7246C30.9106 39.0942 30.5372 40.9413 29.1675 41.8502C27.7978 42.7591 25.9506 42.3857 25.0417 41.016L1.66922 5.79579C0.760312 4.42626 1.13375 2.57907 2.50344 1.67017Z"
          fill="#D77F4A"
        />
        <path
          d="M26.4105 32.313L30.0017 37.7245C30.9106 39.0942 30.5372 40.9412 29.1675 41.8501C27.7978 42.759 25.9506 42.3856 25.0417 41.0159L21.2906 35.3634C22.908 34.1814 24.6211 33.1617 26.4105 32.313Z"
          fill="#C86D36"
        />
        <path
          d="M23.7166 74.1487C21.6105 71.0105 20.3877 67.2298 20.4044 63.1623C20.4484 52.533 29.0848 43.8036 39.7131 43.6516C50.6669 43.495 59.5956 52.3273 59.5956 63.2453C59.5956 67.2806 58.3756 71.0305 56.2844 74.1472C54.9441 76.1448 56.3677 78.8283 58.7733 78.8283H58.7736C59.6877 78.8283 60.5578 78.4166 61.122 77.6973C64.6617 73.1855 66.7878 67.5119 66.8309 61.3428C66.9344 46.5862 54.9497 34.4237 40.1931 34.3198C25.2856 34.2148 13.168 46.2678 13.168 61.1509C13.168 67.3925 15.3 73.1356 18.8745 77.6936C19.4398 78.4144 20.3106 78.8284 21.2266 78.8284C23.6317 78.8281 25.0567 76.1455 23.7166 74.1487Z"
          fill="#FD4755"
        />
        <path
          d="M40.1933 34.3195C39.1803 34.3123 38.181 34.3633 37.1966 34.4655C50.7338 35.8815 61.3289 47.464 61.2317 61.3425C61.1906 67.2128 59.2625 72.6334 56.0263 77.0323C56.4767 78.0561 57.4834 78.8279 58.7734 78.8279H58.7738C59.6878 78.8279 60.5581 78.4162 61.1222 77.697C64.6619 73.1851 66.788 67.5115 66.8311 61.3425C66.9347 46.5859 54.95 34.4234 40.1933 34.3195Z"
          fill="#FB2B3A"
        />
        <path
          d="M68.6997 18.1872C68.3419 18.7264 68.4888 19.4536 69.0281 19.8116C69.5675 20.1694 70.2947 20.0225 70.6525 19.4831L79.3074 6.44236C79.9205 5.51798 80.1366 4.41002 79.9156 3.32283C79.6952 2.23767 79.0656 1.30392 78.1453 0.695329C77.2219 0.0814233 76.1155 -0.135452 75.0291 0.0834545C73.9436 0.302673 73.0083 0.932361 72.3952 1.85595L68.5077 7.71423V4.22142C68.5077 1.93877 66.7517 0.0859545 64.5081 0.00345455C63.3705 -0.0368579 62.2966 0.375954 61.4802 1.16424C60.6627 1.95361 60.2125 3.01439 60.2125 4.15111V20.2161L58.3636 23.0011L55.692 15.5031V10.5353C55.692 8.25048 53.9364 6.39783 51.6941 6.31736C50.5541 6.27502 49.4791 6.69002 48.662 7.47908C47.8452 8.26798 47.3953 9.32767 47.3953 10.4633V16.2194C47.3953 16.7003 47.477 17.1706 47.6369 17.6128L52.6335 31.6334L50.3209 35.118C47.1381 33.8489 43.7202 33.1722 40.202 33.1472C40.1341 33.1467 40.0674 33.1466 39.9994 33.1466C36.4053 33.1466 32.9155 33.8206 29.6711 35.1066L27.3671 31.6341L32.3617 17.6152C32.5228 17.1672 32.6046 16.6977 32.6046 16.2197V10.4635C32.6046 9.32736 32.1544 8.2672 31.3369 7.47814C30.5197 6.68923 29.4441 6.27673 28.3047 6.3172C26.0631 6.3983 24.3074 8.25064 24.3074 10.5345V15.5025L21.6364 22.9997L19.7885 20.2161V4.15048C19.7885 3.01392 19.3383 1.95345 18.521 1.16455C17.7041 0.375954 16.6274 -0.0385767 15.49 0.0042358C13.2486 0.0854858 11.4927 1.93799 11.4927 4.22158V7.71361L7.60565 1.85611C6.99268 0.932673 6.05721 0.303142 4.97143 0.0836108C3.88487 -0.136233 2.77799 0.0809545 1.85628 0.693923C0.932994 1.30564 0.303463 2.24049 0.0837755 3.32642C-0.136224 4.41314 0.0804943 5.52017 0.693619 6.44298L19.6753 35.0478C18.523 34.4267 17.1899 33.8072 15.6811 33.2599C15.0728 33.0399 14.4006 33.3538 14.1799 33.962C13.9592 34.5703 14.2736 35.2425 14.8821 35.4631C18.4149 36.7444 20.92 38.475 22.2228 39.5113C21.5486 40.0672 20.8958 40.6563 20.2678 41.2797C20.2105 41.3367 20.1549 41.395 20.0981 41.4524C15.9585 39.4359 11.4674 38.412 6.73456 38.4081C5.79784 38.4083 4.89674 38.7516 4.2019 39.3631C4.06362 38.5355 3.95659 37.7567 3.87409 37.048C3.77503 36.1945 4.05612 35.3477 4.64549 34.7245C5.23487 34.1016 6.06424 33.7747 6.92284 33.8249C7.88471 33.8824 8.85409 33.9895 9.80409 34.1431C10.4427 34.2483 11.0446 33.8124 11.148 33.1736C11.2514 32.5347 10.8172 31.933 10.1783 31.8297C9.15096 31.6634 8.10268 31.5475 7.06299 31.4855C5.51143 31.3924 4.00956 31.9858 2.94268 33.1142C1.87565 34.2424 1.36659 35.7747 1.54612 37.3189C2.23237 43.2091 4.5219 53.6013 12.1411 58.278C12.0452 59.2259 11.996 60.1842 11.996 61.1502C11.996 61.68 12.0103 62.1963 12.0385 62.6848C12.0756 63.3309 12.6286 63.8233 13.276 63.7872C13.9221 63.75 14.4156 63.1959 14.3783 62.5497C14.3527 62.1059 14.3397 61.6352 14.3397 61.1502C14.3397 54.2628 17.0316 47.7964 21.9196 42.9427C26.7636 38.1325 33.178 35.4903 40.0002 35.4903C40.0619 35.4903 40.1239 35.4905 40.1856 35.4909C47.0088 35.5394 53.4241 38.2625 58.2502 43.1589C63.0756 48.055 65.707 54.5095 65.6592 61.3342C65.6202 67.0647 63.7324 72.4727 60.1989 76.9744C60.0349 77.1839 59.8188 77.3562 59.5688 77.4755C59.3242 77.5936 59.0491 77.6559 58.7734 77.6559C58.0864 77.6559 57.4833 77.2945 57.1605 76.6891C56.8358 76.0805 56.872 75.3741 57.2574 74.7997C59.5534 71.3787 60.7672 67.3833 60.7672 63.2452C60.7672 57.6408 58.5691 52.3878 54.5778 48.4536C50.5878 44.5208 45.3083 42.3977 39.6961 42.48C28.4592 42.6402 19.2797 51.9161 19.2335 63.1575C19.2158 67.3283 20.4296 71.3547 22.7424 74.8002C22.9531 75.1148 23.06 75.4575 23.06 75.8184C23.06 76.702 22.3592 77.6559 21.2272 77.6559C20.6683 77.6559 20.1342 77.3998 19.7977 76.9702C17.6291 74.2052 16.0789 71.0919 15.1902 67.7169C15.0253 67.0911 14.3844 66.718 13.7585 66.882C13.1327 67.0469 12.7589 67.6878 12.9236 68.3138C13.8942 71.9995 15.5866 75.3986 17.953 78.4159C18.7297 79.4075 19.9536 79.9995 21.2271 79.9995C23.53 79.9995 25.4036 78.1239 25.4036 75.8184C25.4036 74.9966 25.1567 74.1936 24.6891 73.4948C22.6375 70.4391 21.5614 66.8678 21.5771 63.1672C21.6181 53.1945 29.7613 44.9656 39.7297 44.8233C44.7027 44.7567 49.3931 46.6338 52.9327 50.1227C56.4735 53.613 58.4235 58.2731 58.4235 63.245C58.4235 66.9163 57.3472 70.4602 55.3111 73.4936C54.4483 74.7798 54.3644 76.4269 55.0925 77.7917C55.8186 79.1536 57.2292 79.9995 58.7734 79.9995C59.3997 79.9995 60.0269 79.8566 60.5822 79.5886C61.1525 79.3169 61.6585 78.9122 62.0434 78.42C65.8994 73.5073 67.9602 67.6047 68.0028 61.3503C68.01 60.3177 67.9591 59.2933 67.8553 58.2797C75.4797 53.603 77.7686 43.2091 78.4541 37.3183C78.6331 35.7744 78.1241 34.2419 77.057 33.1136C75.9902 31.9853 74.4889 31.3909 72.9381 31.485C67.5897 31.8047 63.3441 33.4099 60.3349 35.0317L67.6292 24.0388C67.987 23.4995 67.8402 22.7724 67.3006 22.4145C66.7617 22.0567 66.0342 22.2036 65.6764 22.7431L55.6053 37.9206C54.6027 37.2414 53.5609 36.632 52.4869 36.0927L54.9106 32.4403C55.1149 32.1325 55.162 31.7467 55.038 31.3989L49.8427 16.8211C49.7739 16.6303 49.7389 16.4278 49.7389 16.2192V10.4635C49.7389 9.96939 49.9345 9.50845 50.2899 9.16533C50.6455 8.82205 51.1139 8.64314 51.611 8.6597C52.5689 8.69408 53.3481 9.53548 53.3481 10.5355V15.7056C53.3481 15.8395 53.3711 15.9727 53.4161 16.0991L56.9411 25.9927C57.09 26.4106 57.4622 26.7084 57.9025 26.7625C58.3417 26.8161 58.7759 26.617 59.0213 26.2475L62.3603 21.2178C62.4878 21.0258 62.5559 20.8002 62.5559 20.5697V4.15111C62.5559 3.6558 62.752 3.19392 63.1078 2.85033C63.4627 2.50767 63.9288 2.32845 64.4238 2.3458C65.3833 2.38111 66.1638 3.22252 66.1638 4.22158V11.5997C66.1638 12.1166 66.5024 12.5724 66.9972 12.7217C67.4925 12.8706 68.0263 12.6785 68.312 12.2478L74.3478 3.15252C74.6145 2.75064 75.0213 2.47674 75.4931 2.38142C75.9649 2.28674 76.4461 2.38064 76.8499 2.64939C77.2499 2.91392 77.523 3.31908 77.6188 3.79017C77.7149 4.2633 77.6209 4.74533 77.3544 5.1472L68.6997 18.1872ZM12.5203 55.7153C8.97502 53.1536 6.83659 49.0364 5.54815 45.0102C5.42659 44.1286 5.32799 43.2324 5.25627 42.3447C5.22315 41.9299 5.36034 41.5331 5.64268 41.2277C5.92612 40.9209 6.31252 40.7522 6.73096 40.7522H6.73252C10.8697 40.7556 14.803 41.6024 18.4481 43.2672C15.4363 46.8823 13.4146 51.142 12.5203 55.7153ZM2.38081 3.79127C2.47628 3.31955 2.74971 2.91345 3.15206 2.64689C3.55362 2.37986 4.03487 2.28564 4.5069 2.38095C4.97909 2.47642 5.38612 2.75033 5.65284 3.1522L11.6883 12.2466C11.9742 12.6772 12.508 12.8699 13.0031 12.7205C13.498 12.5713 13.8366 12.1153 13.8366 11.5985V4.22158C13.8366 3.22236 14.6164 2.38127 15.5739 2.34642C16.0717 2.32736 16.5381 2.50814 16.8933 2.85064C17.2489 3.19392 17.4449 3.65548 17.4449 4.15033V20.5699C17.4449 20.8005 17.513 21.0259 17.6403 21.218L20.9785 26.2464C21.2238 26.6159 21.6566 26.8152 22.0974 26.7614C22.5377 26.7074 22.9099 26.4094 23.0588 25.9914L26.5831 16.0984C26.6281 15.9722 26.6511 15.8392 26.6511 15.7052V10.5349C26.6511 9.53564 27.431 8.69439 28.3886 8.6597C28.8836 8.64486 29.3539 8.82142 29.7094 9.1647C30.065 9.50798 30.261 9.96923 30.261 10.4636V16.2199C30.261 16.4267 30.2258 16.6294 30.1553 16.8255L24.9627 31.4C24.8388 31.7478 24.886 32.1336 25.09 32.4413L27.5011 36.0752C26.4256 36.6125 25.3841 37.2222 24.3805 37.8995L2.64612 5.14673C2.37956 4.74517 2.28534 4.26392 2.38081 3.79127ZM67.4734 55.7195C66.5739 51.1625 64.5439 46.9056 61.521 43.2822C65.1761 41.6072 69.12 40.7553 73.2681 40.752H73.2697C73.688 40.752 74.0742 40.9209 74.3577 41.2275C74.6402 41.5331 74.7774 41.9297 74.7442 42.3436C74.6724 43.2333 74.5736 44.1309 74.4517 45.0134C73.1624 49.0409 71.0224 53.1588 67.4734 55.7195ZM73.0781 33.825C73.9353 33.7741 74.765 34.1016 75.3541 34.7245C75.9436 35.3478 76.225 36.1949 76.1258 37.0481C76.0433 37.7569 75.9364 38.5358 75.7983 39.3636C75.1038 38.7525 74.202 38.4084 73.2694 38.4084C73.2681 38.4084 73.2669 38.4084 73.2658 38.4084C68.5227 38.4123 64.0216 39.4414 59.8724 41.4678C59.1944 40.783 58.4874 40.138 57.7549 39.5327C59.8664 37.8542 65.2144 34.2952 73.0781 33.825Z"
          fill="black"
        />
      </g>
      <defs>
        <clipPath id="clip0_123_239">
          <rect width="80" height="80" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

export function ChristmasLights() {
  return (
    <div className="fixed top-0 left-0 w-full h-12 z-50 flex justify-between items-start pointer-events-none overflow-hidden px-4">
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className={`w-3 h-3 rounded-full shadow-md animate-pulse ${
            i % 3 === 0
              ? "bg-red-500 shadow-red-500/50"
              : i % 3 === 1
                ? "bg-green-500 shadow-green-500/50"
                : "bg-yellow-400 shadow-yellow-400/50"
          }`}
          style={{
            animationDuration: `${1 + Math.random()}s`,
            animationDelay: `${Math.random()}s`,
            marginTop: `${Math.random() * 10 - 5}px`,
          }}
        />
      ))}
      <div className="absolute top-0 left-0 w-full h-1 bg-gray-800/20 -z-10" />
    </div>
  )
}

export function SantaSleigh({
  active,
  onEnd,
}: {
  active?: boolean
  onEnd?: () => void
}) {
  if (!active) return null
  return (
    <div
      className="fixed top-20 left-0 z-40 pointer-events-none animate-[fly-across_20s_linear_forwards]"
      onAnimationEnd={onEnd}
    >
      <div className="text-6xl filter drop-shadow-lg transform -scale-x-100">
        🎅🦌🦌
      </div>
    </div>
  )
}

export function ChristmasScene() {
  const [santaActive, setSantaActive] = React.useState(false)
  const isKeyboardOpen = useKeyboardOpen()

  return (
    <>
      <Snow />
      <ChristmasLights />
      {!isKeyboardOpen && (
        <ChristmasTree onInteract={() => setSantaActive(true)} />
      )}
      <SantaSleigh active={santaActive} onEnd={() => setSantaActive(false)} />
    </>
  )
}
